import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { EditorModule } from 'primeng/editor';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { CatalogueApiService, DiscussionApiService } from '@angular-workspace/services';
import { FormatService } from '../../services/format.service';

export interface AssetSearchResult {
  id: string;
  name: string;
  type: string;
}

export interface UploadedAttachmentOffcanvas {
  file?: File;
  key: string;
  name?: string;
  uploading?: boolean;
  error?: string;
}

export interface DiscussionFormData {
  category: string;
  title: string;
  assetCategory: string;
  assetId: string;
  assetName: string;
  content: string;
  tags: string[];
  attachments: File[] | string[]; // Keys (string[]) after upload for API payload
  /** Existing attachments from API (edit mode): key for payload, name for display */
  existingAttachments?: Array<{ key: string; name: string }>;
}

@Component({
  selector: 'app-create-discussion-offcanvas',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    EditorModule
  ],
  templateUrl: './create-discussion-offcanvas.component.html',
  styleUrl: './create-discussion-offcanvas.component.scss',
})
export class CreateDiscussionOffcanvasComponent implements OnInit, OnDestroy, OnChanges {
  @Input() visible: boolean = false;
  @Input() initialValues?: Partial<DiscussionFormData>;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() publish = new EventEmitter<DiscussionFormData>();
  @Output() close = new EventEmitter<void>();

  private catalogueApiService = inject(CatalogueApiService);
  private discussionApiService = inject(DiscussionApiService);
  private fb = inject(FormBuilder);
  private formatService = inject(FormatService);
  private searchSubject = new Subject<string>();

  discussionForm!: FormGroup;
  tagInput: string = '';
  assetSearchResults: AssetSearchResult[] = [];
  showAssetSearchResults: boolean = false;
  selectedAsset: AssetSearchResult | null = null;
  attachmentFiles: UploadedAttachmentOffcanvas[] = [];
  attachmentSessionId: string = '';
  assetSearchQuery: string = '';

  readonly ALLOWED_ATTACHMENT_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];
  readonly MAX_ATTACHMENT_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

  getAllowedFileTypesLabel(): string {
    return 'PDF, JPG, PNG';
  }

  categoryOptions = [
    { value: 'GETTING_STARTED', label: 'Getting Started' },
    { value: 'PUBLIC', label: 'Public' },
    { value: 'GENERAL', label: 'General' },
    { value: 'PRODUCT_FEEDBACK', label: 'Feedback' },
    { value: 'PRODUCT_ANNOUNCEMENTS', label: 'Announcement' }
  ];

  assetCategoryOptions = [
    { value: 'Dataset', label: 'Datasets' },
    { value: 'Model', label: 'Models' },
    { value: 'Others', label: 'Others' }
  ];

  editorModules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['code-block']
      ],
      handlers: {}
    }
  };

  /**
   * Intercept paste so only plain text is inserted (HTML tags are stripped).
   * Reduces XSS risk and keeps pasted content from introducing unwanted markup.
   */
  onEditorInit(event: any): void {
    const editor = event?.editor;
    if (!editor) return;
    const clipboard = editor.getModule('clipboard');
    if (!clipboard) return;
    const originalPaste = clipboard.dangerouslyPasteHTML?.bind(clipboard);
    if (typeof originalPaste !== 'function') return;
    clipboard.dangerouslyPasteHTML = (html: string, source?: string) => {
      const plain = this.formatService.stripAllHtml(html ?? '');
      const escaped = plain
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
      const safeHtml = plain ? `<p>${escaped}</p>` : '<p><br></p>';
      return originalPaste(safeHtml, source);
    };
  }

  readonly CONTENT_MAX_LENGTH = 5000;

  ngOnInit(): void {
    console.log('CreateDiscussionOffcanvas - ngOnInit, visible:', this.visible);
    this.initDiscussionForm();
    this.setupAssetSearch();
    this.attachmentSessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Debug logging
    if (changes['visible']) {
      console.log('CreateDiscussionOffcanvas - visible changed:', changes['visible'].currentValue, 'previous:', changes['visible'].previousValue);
    }
    
    // When visible becomes true or initialValues change, apply them
    if (changes['visible'] && this.visible && this.initialValues) {
      this.applyInitialValues();
    } else if (changes['initialValues'] && this.visible && this.initialValues) {
      this.applyInitialValues();
    }
  }

  ngOnDestroy(): void {
    // Clean up subscription
    this.searchSubject.complete();
  }

  initDiscussionForm(): void {
    this.discussionForm = this.fb.group({
      category: ['', [Validators.required]],
      title: ['', [Validators.required, Validators.maxLength(200)]],
      assetCategory: [''],
      assetId: [''],
      assetName: [''],
      content: ['', [Validators.required]],
      tags: [[], [Validators.required]],
      attachments: [[]]
    });
  }

  applyInitialValues(): void {
    if (this.initialValues) {
      this.discussionForm.patchValue({
        category: this.initialValues.category || '',
        assetCategory: this.initialValues.assetCategory || '',
        assetId: this.initialValues.assetId || '',
        assetName: this.initialValues.assetName || '',
        title: this.initialValues.title || '',
        content: this.initialValues.content || '',
        tags: this.initialValues.tags || []
      });
      
      if (this.initialValues.assetName) {
        this.assetSearchQuery = this.initialValues.assetName;
        this.selectedAsset = this.initialValues.assetId ? {
          id: this.initialValues.assetId,
          name: this.initialValues.assetName,
          type: this.initialValues.assetCategory || ''
        } : null;
      }
      
      if (this.initialValues.existingAttachments && this.initialValues.existingAttachments.length > 0) {
        this.attachmentFiles = this.initialValues.existingAttachments.map(a => ({
          key: a.key,
          name: a.name,
          uploading: false
        }));
      }
    }
  }

  getAttachmentDisplayName(att: UploadedAttachmentOffcanvas): string {
    return att.file?.name ?? att.name ?? att.key ?? 'Attachment';
  }

  setupAssetSearch(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query: string) => {
        if (!query || query.length < 2) {
          this.assetSearchResults = [];
          this.showAssetSearchResults = false;
          return Promise.resolve([]);
        }
        return this.searchAssets(query);
      })
    ).subscribe();
  }

  async searchAssets(query: string): Promise<AssetSearchResult[]> {
    const assetCategory = this.discussionForm.get('assetCategory')?.value;
    if (!assetCategory || assetCategory === 'Others') {
      this.assetSearchResults = [];
      this.showAssetSearchResults = false;
      return Promise.resolve([]);
    }

    try {
      const assetTypeMap: { [key: string]: string[] } = {
        'Dataset': ['Dataset'],
        'Model': ['Model'],
        'Usecase': ['Usecase']
      };

      const assetTypes = assetTypeMap[assetCategory] || [];
      
      const response = await this.catalogueApiService.searchAllAssets(
        query,
        assetTypes,
        [],
        1,
        10,
        'name:asc',
        []
      );

      if (response && response.items) {
        const results = response.items.map((item: any) => ({
          id: item.id || item['@id'] || '',
          name: item.name || item.label || '',
          type: assetCategory
        }));
        this.assetSearchResults = results;
        this.showAssetSearchResults = true;
        return results;
      } else {
        this.assetSearchResults = [];
        this.showAssetSearchResults = true;
        return [];
      }
    } catch (error) {
      console.error('Error searching assets:', error);
      this.assetSearchResults = [];
      this.showAssetSearchResults = true;
      return [];
    }
  }

  onAssetSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const query = input.value.trim();
    this.assetSearchQuery = query;
    this.searchSubject.next(query);
  }

  onAssetSearchBlur(): void {
    // Delay hiding results to allow click events to fire
    setTimeout(() => {
      this.showAssetSearchResults = false;
    }, 200);
  }

  onAssetSearchFocus(): void {
    const query = this.assetSearchQuery.trim();
    if (query.length < 2) return;
    if (this.assetSearchResults.length > 0 || this.showAssetSearchResults) {
      this.showAssetSearchResults = true;
    } else {
      this.searchSubject.next(query);
    }
  }

  selectAsset(asset: AssetSearchResult): void {
    this.selectedAsset = asset;
    this.discussionForm.patchValue({
      assetId: asset.id,
      assetName: asset.name
    });
    this.showAssetSearchResults = false;
    this.assetSearchQuery = asset.name;
  }

  clearAsset(): void {
    this.selectedAsset = null;
    this.assetSearchQuery = '';
    this.discussionForm.patchValue({
      assetId: '',
      assetName: ''
    });
  }

  onAssetCategoryChange(): void {
    this.clearAsset();
    this.assetSearchResults = [];
    this.showAssetSearchResults = false;
  }

  getAssetCategoryLabel(value: string): string {
    const option = this.assetCategoryOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  getAssetSearchEmptyMessage(): string {
    const category = this.discussionForm.get('assetCategory')?.value;
    const label = category
      ? this.getAssetCategoryLabel(category)
      : 'assets';
    return `No ${label} found.`;
  }

  getTags(): string[] {
    return this.discussionForm.get('tags')?.value || [];
  }

  addTag(): void {
    const tag = this.sanitizeTagInput(this.tagInput.trim());
    this.tagInput = tag;
    if (!tag || !this.isValidTagValue(tag)) {
      return;
    }
    const currentTags = this.getTags();
    if (!currentTags.some(t => t.toLowerCase() === tag.toLowerCase())) {
      currentTags.push(tag);
      this.discussionForm.patchValue({ tags: currentTags });
      this.discussionForm.get('tags')?.markAsTouched();
      this.tagInput = '';
    }
  }

  removeTag(tagToRemove: string): void {
    const currentTags = this.getTags();
    const updatedTags = currentTags.filter(tag => tag !== tagToRemove);
    this.discussionForm.patchValue({ tags: updatedTags });
    this.discussionForm.get('tags')?.markAsTouched();
  }

  onTagInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addTag();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    if (event.key.length > 1) {
      return;
    }
    if (!/^[a-zA-Z0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onTagInputChange(): void {
    const sanitized = this.sanitizeTagInput(this.tagInput);
    if (sanitized !== this.tagInput) {
      this.tagInput = sanitized;
    }
  }

  onTagInputPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    this.tagInput = this.sanitizeTagInput(this.tagInput + pasted);
  }

  canAddTag(): boolean {
    const tag = this.tagInput.trim();
    return tag.length > 0 && this.isValidTagValue(tag);
  }

  private sanitizeTagInput(value: string): string {
    return value.replace(/[^a-zA-Z0-9]/g, '');
  }

  private isValidTagValue(tag: string): boolean {
    return /^[a-zA-Z0-9]+$/.test(tag);
  }

  private getAttachmentValidationError(file: File): string | null {
    const ext = file.name.includes('.') ? '.' + file.name.split('.').pop()!.toLowerCase() : '';
    if (!this.ALLOWED_ATTACHMENT_EXTENSIONS.some(e => ext === e)) {
      return `Allowed formats: ${this.getAllowedFileTypesLabel()}`;
    }
    if (file.size > this.MAX_ATTACHMENT_SIZE_BYTES) {
      return `Max file size 5MB (${(file.size / (1024 * 1024)).toFixed(2)}MB selected)`;
    }
    return null;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const files = Array.from(input.files);
    input.value = '';

    const newAttachments: UploadedAttachmentOffcanvas[] = files.map(file => {
      const validationError = this.getAttachmentValidationError(file);
      return {
        file,
        key: '',
        name: file.name,
        uploading: !validationError,
        error: validationError ?? undefined
      };
    });
    this.attachmentFiles = [...this.attachmentFiles, ...newAttachments];
    this.discussionForm.patchValue({ attachments: this.attachmentFiles });

    const sessionId = this.attachmentSessionId;
    files.forEach((file, index) => {
      if (this.getAttachmentValidationError(file)) return;
      const attachmentIndex = this.attachmentFiles.length - files.length + index;
      (async () => {
        try {
          const presignedResponse = await this.discussionApiService.discussionAttachment(
            sessionId,
            { type_of_upload: 'attachment', file_name: file.name },
            file
          );
          const objectKey = presignedResponse?.meta?.object_key ?? presignedResponse?.data?.object_key ?? (presignedResponse as any)?.object_key;
          if (!objectKey) {
            throw new Error(presignedResponse?.message ?? (presignedResponse as any)?.detail ?? 'No object key in response');
          }
          if (this.attachmentFiles[attachmentIndex]) {
            const att = this.attachmentFiles[attachmentIndex];
            this.attachmentFiles = this.attachmentFiles.slice();
            this.attachmentFiles[attachmentIndex] = {
              ...att,
              key: objectKey,
              name: att.file?.name ?? att.name,
              uploading: false
            };
            this.discussionForm.patchValue({ attachments: this.attachmentFiles });
          }
        } catch (error: any) {
          console.error('Error uploading file:', file.name, error);
          const message =
            error?.error?.detail ??
            error?.error?.message ??
            (typeof error?.error === 'string' ? error.error : null) ??
            error?.message ??
            'Failed to upload file';
          if (this.attachmentFiles[attachmentIndex]) {
            this.attachmentFiles = this.attachmentFiles.slice();
            this.attachmentFiles[attachmentIndex] = {
              ...this.attachmentFiles[attachmentIndex],
              uploading: false,
              error: message
            };
            this.discussionForm.patchValue({ attachments: this.attachmentFiles });
          }
        }
      })();
    });
  }

  removeAttachment(index: number): void {
    this.attachmentFiles.splice(index, 1);
    this.discussionForm.patchValue({ attachments: this.attachmentFiles });
  }

  get contentLength(): number {
    const content = this.discussionForm?.get('content')?.value || '';
    const div = document.createElement('div');
    div.innerHTML = content;
    return div.textContent?.length || 0;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  closeOffcanvas(): void {
    this.visibleChange.emit(false);
    this.close.emit();
    this.resetForm();
  }

  resetForm(): void {
    this.discussionForm.reset();
    this.tagInput = '';
    this.selectedAsset = null;
    this.assetSearchQuery = '';
    this.assetSearchResults = [];
    this.showAssetSearchResults = false;
    this.attachmentFiles = [];
    
    // Re-apply initial values if they exist
    if (this.initialValues) {
      setTimeout(() => this.applyInitialValues(), 0);
    }
  }

  getCategoryLabel(value: string): string {
    const option = this.categoryOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  publishDiscussion(): void {
    if (this.discussionForm.valid) {
      const uploadedKeys = this.attachmentFiles
        .filter(att => att.key && !att.error)
        .map(att => att.key);
      const formData: DiscussionFormData = {
        category: this.discussionForm.get('category')?.value || '',
        title: this.formatService.stripAllHtml(this.discussionForm.get('title')?.value || '').trim(),
        assetCategory: this.discussionForm.get('assetCategory')?.value || '',
        assetId: this.discussionForm.get('assetId')?.value || '',
        assetName: this.discussionForm.get('assetName')?.value || '',
        content: this.discussionForm.get('content')?.value || '',
        tags: this.discussionForm.get('tags')?.value || [],
        attachments: uploadedKeys
      };
      this.publish.emit(formData);
    } else {
      Object.keys(this.discussionForm.controls).forEach(key => {
        this.discussionForm.get(key)?.markAsTouched();
      });
    }
  }
}
